import { grammarFigureUrl, type GrammarEntry, type GrammarExample } from "@/lib/search";
import { relatedChips } from "@/components/dictionary/resultKind";

import { FormationBlock, NotesBody } from "./GrammarBlocks";
import { notesBlocks } from "./grammarNotes";
import { posAddsDetail, posFull } from "./grammarPos";

/**
 * An opened DoJG entry — a dictionary SPREAD (日本-524, artboard A 「見開き」, Alex 2 Sep 2026).
 *
 * **What this replaces, measured.** 日本-519 drew every block in one column under a 48px label
 * gutter, and 解説 was `text-meta` — 14px, the type ramp's FLOOR — at full bleed: a **1003px
 * measure at 1147 and 1456px at 1600**, which is 150 and 210 characters a line, on a field that
 * averages 1,549 characters and reaches 9,646. Every line of it was one style, so a numbered note,
 * its examples, their translations and a 【Related Expressions】 essay were typographically
 * identical. 接続 was a single run-on paragraph of the flattened field.
 *
 * **The spread.** A reference column on the left — 接続, 基本文, 例文, 関連 — **sticky**, while
 * 解説 reads down the right at 16px in a ~75ch measure. The stickiness is the point rather than an
 * effect: 解説 cites its own examples constantly (*"as in Exs. (b), (d)"*, *"the Key Sentence (A)
 * pattern"*), and before this you had to scroll back up to a list you had already passed. Now (b)
 * and (d) are beside the sentence that names them.
 *
 * **`examples[].label` is drawn for the first time.** It has been in the data since 日本-498 and
 * was rendered nowhere, which is what made those citations unfollowable: `ksa`/`ksb` are the book's
 * Key Sentences and `a`…`k` its examples, and 解説 refers to both by exactly those letters.
 *
 * **Two columns is a re-flow, not a second arrangement**, so it is `lg:` classes rather than
 * `useWideLayout`. `stats/layout.tsx` documents the distinction and the cost of getting it wrong:
 * a JS breakpoint remounts the subtree, which is only worth paying when the two layouts group the
 * panels differently. These do not — the same blocks, in one column or two.
 *
 * Preserved from 日本-519 and pinned by `check-grammar.mjs`: the body **hangs under the headword**
 * (indenting to the meaning column cost 274px a line), **解説 is open** and **every example is
 * drawn** — no ＋N例 fold, because this screen exists to read the entry.
 */

interface Props {
  entry: GrammarEntry;
  /** 390px: one column, and every block stacks its label above its content. */
  isPhone?: boolean;
}

/** A labelled block. The label sits above its content in both arrangements — see `Body`. */
function Block({
  label,
  children,
  className = "",
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section className={`flex flex-col gap-[7px] ${className}`}>
      <h3 className="m-0 font-jp text-meta font-semibold tracking-[.1em] text-ink-4">{label}</h3>
      <div className="min-w-0">{children}</div>
    </section>
  );
}

/**
 * Mark the construction inside its example.
 *
 * `pattern` holds the span AS INFLECTED in this sentence, which is the whole reason the dataset
 * carries it — `あげる (1)` gives `あげた` and `あげました`, and no string match on the concept would
 * find either. Split rather than regex, on `splitOnTarget`'s rule: the span is arbitrary text off
 * the dataset, so `String.prototype.split` takes it literally and a `(` in a pattern can never
 * become one.
 */
function marked(text: string, patterns: string[]) {
  const span = patterns.find((p) => p && text.includes(p));
  if (!span) return text;
  return text
    .split(span)
    .flatMap((part, index) =>
      index === 0
        ? [part]
        : [
            <span key={index} data-check="grammar.pattern" className="text-[color:var(--accent-text)]">
              {span}
            </span>,
            part,
          ],
    );
}

/**
 * One example, with the book's own label in the margin.
 *
 * The label is a fixed 26px column so a run of examples lines up on its sentences rather than
 * ragging with the label widths — `(ksa)` is wider than `(a)`, and a ragged left edge on a column
 * of Japanese is what makes a list read as a paragraph.
 */
function ExampleLine({ example, label }: { example: GrammarExample; label: string | null }) {
  return (
    <div data-check="grammar.example" className="flex gap-[9px]">
      {label !== null && (
        <span
          data-check="grammar.example.label"
          className="w-[26px] shrink-0 pt-[3px] font-mono text-meta text-ink-4"
        >
          {label}
        </span>
      )}
      <div className="flex min-w-0 flex-1 flex-col gap-[3px]">
        <span className="font-jp text-[17px]/[1.6] text-ink-2">
          {marked(example.ja, example.pattern)}
        </span>
        {example.en && <span className="text-meta text-ink-4">{example.en}</span>}
      </div>
    </div>
  );
}

/**
 * The book's own label for an example: `ksa` → `(A)`, `a` → `(a)`.
 *
 * **Key Sentences are lettered in CAPITALS and examples in lower case, which is how 解説 cites
 * them** — *"the Key Sentence (A) pattern"* against *"as in Exs. (b), (d)"*. Rendering both in one
 * case would make the two sets collide at (a).
 */
function exampleLabel(label: string): string | null {
  const key = /^ks([a-z])?$/.exec(label);
  if (key) return key[1] ? `(${key[1].toUpperCase()})` : "(A)";
  if (/^[a-z]$/.test(label)) return `(${label})`;
  return null;
}

const isKeySentence = (example: GrammarExample) => example.label.startsWith("ks");

/**
 * A parsed table.
 *
 * **This is what a `kind: "table"` FIGURE draws as** — see `figuresToDraw`. Real text rather than a
 * scan: selectable, legible at any width, and it reflows on a phone where a PNG of a table cannot.
 */
function Table({
  columns,
  rows,
  title,
}: {
  columns: string[];
  rows: string[][];
  title: string | null;
}) {
  return (
    <div data-check="grammar.table" className="flex flex-col gap-[7px]">
      {title && <span className="text-meta text-ink-4">{title}</span>}
      {/* Scrolls inside its own box — the app forbids the page scrolling sideways at any width. */}
      <div className="overflow-x-auto rounded-[var(--r-2)] border border-line bg-sunken">
        <table className="w-full border-collapse">
          <thead>
            <tr>
              {columns.map((column, index) => (
                <th
                  key={index}
                  className="whitespace-nowrap border-b border-line-strong px-3 py-2 text-left text-meta font-semibold text-ink-4"
                >
                  {column}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {row.map((cell, cellIndex) => (
                  <td
                    key={cellIndex}
                    className={`whitespace-nowrap border-b border-line-soft px-3 py-2 ${
                      cellIndex === 0
                        ? "text-meta text-ink-4"
                        : "font-jp text-[16px] text-ink-2"
                    }`}
                  >
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * Which figures draw as IMAGES, and which are replaced by a parsed table.
 *
 * **The rule falls out of the data rather than being a hand list.** Measured over the whole corpus:
 * 11 figures across 7 entries, 5 tables across 4. Six of the eleven are `kind: "table"`, and every
 * one of those is claimed by a `grammar_table` row through its `figure` or `figureContinued` — so a
 * figure a table names is drawn AS that table, and the other five (`scale` ×2, `diagram`, `stack`,
 * `list`) have no parsed equivalent and can only be images.
 *
 * Doing it this way rather than by `kind` alone matters: a future dataset that parses one of the
 * five gains a real table with no code change, and one that adds an unparsed table figure still
 * shows the scan rather than nothing.
 */
export function figuresToDraw(entry: GrammarEntry): GrammarEntry["figures"] {
  const claimed = new Set<string>();
  for (const table of entry.tables) {
    if (table.figure) claimed.add(table.figure);
    if (table.figureContinued) claimed.add(table.figureContinued);
  }
  return entry.figures.filter((figure) => !claimed.has(figure.file));
}

export function GrammarEntryBody({ entry, isPhone = false }: Props) {
  const chips = relatedChips(entry.related);
  const figures = figuresToDraw(entry);
  const blocks = entry.notesText ? notesBlocks(entry.notesText) : [];

  const keySentences = entry.examples.filter(isKeySentence);
  const examples = entry.examples.filter((example) => !isKeySentence(example));

  // **Absent blocks are STATED rather than silently omitted.** 106 entries carry no 接続 and 119 no
  // 関連, so an entry that simply stops reads as truncated — one quiet line says the book has none.
  const missing = [
    !entry.formation ? "接続の記載はありません" : null,
    chips.length === 0 ? "関連項目はありません" : null,
  ].filter(Boolean) as string[];

  const reference = (
    <div className="flex flex-col gap-[16px]">
      {/* **品詞 only when the full string says more than the row line already does** — 166 of the
          629 entries are a bare 句, where the two would be identical and the row would repeat. */}
      {posAddsDetail(entry.partOfSpeech) && (
        <Block label="品詞">
          <span data-check="grammar.pos" className="font-jp text-meta text-ink-2">
            {posFull(entry.partOfSpeech)}
          </span>
        </Block>
      )}

      {entry.usage && (
        <Block label="用法">
          <p className="m-0 text-meta text-ink-3">{entry.usage}</p>
        </Block>
      )}

      {entry.formation && (
        <Block label="接続">
          <FormationBlock formation={entry.formation} />
        </Block>
      )}

      {/* **基本文 is set apart from 例文**, because the book treats them as different things and
          解説 cites them differently — "the Key Sentence (A) pattern" against "as in Exs. (b)". */}
      {keySentences.length > 0 && (
        <Block label="基本文">
          <div data-check="grammar.keysentences" className="flex flex-col gap-[11px]">
            {keySentences.map((example, index) => (
              <ExampleLine key={index} example={example} label={exampleLabel(example.label)} />
            ))}
          </div>
        </Block>
      )}

      {examples.length > 0 && (
        <Block label="例文">
          {/* **Every one**, unfolded. An entry averages 8.6 and the ＋N例 fold existed to keep a
              results list readable; this screen exists to read them. */}
          <div className="flex flex-col gap-[11px]">
            {examples.map((example, index) => (
              <ExampleLine key={index} example={example} label={exampleLabel(example.label)} />
            ))}
          </div>
        </Block>
      )}

      {chips.length > 0 && (
        <Block label="関連">
          <div className="flex flex-wrap items-center gap-2">
            {chips.map((chip) => (
              <span
                key={chip}
                data-check="grammar.related"
                className="rounded-[var(--r-2)] border border-line-strong bg-line-soft px-[10px] py-[5px] font-jp text-meta text-ink-2"
              >
                {chip}
              </span>
            ))}
          </div>
        </Block>
      )}
    </div>
  );

  const commentary = (
    <div className="flex flex-col gap-[16px]">
      {blocks.length > 0 && (
        <Block label="解説">
          {/* **16px against the 14px ramp floor it sat at.** The ~75ch measure is the COLUMN's
              (see the wrapper below), not this block's — one rule for everything the column holds,
              rather than the same number repeated on 解説 and 図表 where the two could drift. */}
          <NotesBody blocks={blocks} />
        </Block>
      )}

      {(entry.tables.length > 0 || figures.length > 0) && (
        <Block label="図表">
          <div className="flex flex-col gap-[14px]">
            {entry.tables.map((table, index) => (
              <Table key={`t${index}`} columns={table.columns} rows={table.rows} title={table.title} />
            ))}
            {figures.map((figure) => (
              <figure key={figure.file} className="m-0 flex flex-col gap-1">
                <img
                  data-check="grammar.figure"
                  src={grammarFigureUrl(figure.file)}
                  alt={figure.caption ?? ""}
                  className="max-w-full rounded-[var(--r-2)] border border-line"
                />
                {figure.caption && (
                  <figcaption className="text-meta text-ink-4">{figure.caption}</figcaption>
                )}
              </figure>
            ))}
          </div>
        </Block>
      )}
    </div>
  );

  return (
    <div className="flex flex-col">
      <div
        data-check="grammar.body"
        className={`flex flex-col gap-[22px] pb-[18px] pt-[6px] lg:flex-row lg:gap-[30px] ${
          isPhone ? "px-[14px]" : "px-[18px]"
        }`}
      >
        {/* **Sticky at `lg`, and NOT at `2xl` — the two widths want different things** (Alex,
            3 Sep 2026: *"for this window specifically, they don't need to scroll independently,
            both columns should scroll as one"*).

            At `lg` it has to pin, and three things are needed to make it. `self-start`, because a
            flex item stretches by default and a box as tall as its scrollport can never move
            within it. And a **max-height with its own scroll**: 間・あいだ(に)'s reference column is
            **1,412px inside a 983px scrollport** at 1147, so `position: sticky` was set and did
            nothing — measured, `top` went 207 → **-360** on one scroll. A sticky box only pins
            once it is shorter than what it scrolls in. The `calc(100dvh-181px)` is measured: the
            list's scrollport starts a constant 157px down the window at 900, 1000 and 1400, and
            there is no CSS unit for *the height of my nearest scrollport*.

            At `2xl` all three come off. The column is wider there, so it is shorter — 1,151px
            against 1,412 — but still over the port, which means the internal scrollbar was firing
            on exactly the widths with the most room to spare. **The cost is real and is Alex's
            call**: the examples scroll away as 解説 is read, which is what the pin existed to
            prevent. What buys it back is that both columns are the same width at `2xl`, so the
            examples sit level with the notes that cite them rather than above a column three times
            their length.

            No ancestor may carry `transform`, `filter` or `contain` where it IS sticky: any of
            them makes it stick to that ancestor instead, which reads as the column not sticking. */}
        <div
          data-check="grammar.reference"
          className="min-w-0 lg:sticky lg:top-[8px] lg:max-h-[calc(100dvh-181px)] lg:w-[360px] lg:shrink-0 lg:self-start lg:overflow-y-auto lg:overscroll-contain 2xl:static 2xl:max-h-none 2xl:w-auto 2xl:max-w-none 2xl:flex-1 2xl:shrink 2xl:overflow-visible"
        >
          {reference}
        </div>
        {/* **The cap belongs to the COLUMN, not to the text inside it** (Alex, 3 Sep 2026:
            *"very poor split on desktop wide, fine on desktop narrow"*). `flex-1` handed the
            commentary every pixel the reference column did not take while `max-w-[75ch]` held the
            prose at 720px, so the surplus became a void INSIDE the column and grew with the
            window: measured on basic-011, 348px of empty column at 1600, **698px at 1950 and
            1,041px at 2293** — 46% of the row, with the reference column still at 420px and its
            例文 wrapping. Capping the column instead means the slack is a page margin rather than
            an empty half of a two-column spread, and it is stated once here rather than repeated
            on every block the column holds.

            **At `2xl` the two columns are the same width and they FILL the row** — `flex-1` with
            the measure cap lifted, so they split whatever the window has: 750/750 at 1600,
            1091/1091 at 2293, 1338/1338 at 2788. Alex asked for 50/50 three times (3 Sep 2026:
            *"Why not 50/50?"*, *"I'd like the columns to be equal width"*, and then, against a
            2788px screenshot of a build already measuring 750/750, *"the two columns are not the
            same width"*).

            **What he was reading is the ROW, not the columns**, and that is the thing to know
            before anyone caps this again. At 2788 the row header runs the full 2742px — the
            concept, the 品詞, the meaning and `p.81` — while a pair of 75ch columns stopped at
            1593, leaving **1,194px of empty row** to the right of 解説 with no edge marking where
            the column ended. 接続 draws a border so the left column read as bounded; 解説 has no
            right edge, so it read as running to the row's. Measured equal, seen unequal — and the
            check now asserts `rowSlack` for exactly that, because column equality alone passed
            against the build he reported (720/720 with **759px** of empty row at 2293).

            **The cost is stated rather than hidden, because it is the number this ticket was
            raised to bring down**: 解説 at 16px in 1338px is ~170 characters a line, against the
            150 the old full-bleed single column gave at 1147 and the ~92 a 75ch cap gives. Below
            1536 nothing moves; only the ultrawide trades measure for a filled row. If it reads
            long, the fix is a cap on the BODY — one max-width the two columns split, so they stay
            equal and the slack becomes a page margin — never a cap per column, which is what
            produced the void above. */}
        <div data-check="grammar.commentary" className="min-w-0 lg:max-w-[75ch] lg:flex-1 2xl:max-w-none">
          {commentary}
        </div>
      </div>
      {missing.length > 0 && (
        <div
          data-check="grammar.missing"
          className={`flex gap-5 border-t border-line-soft py-[11px] ${isPhone ? "px-[14px]" : "px-[18px]"}`}
        >
          {missing.map((line) => (
            <span key={line} className="font-jp text-meta text-ink-off">
              {line}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
